import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import * as React from "react";
import DataGrid from "../../components/DataGrid/DataGrid.tsx";
import {
  Container,
  Toolbar,
  Stack,
  Box,
  Paper,
  Tooltip,
  InputBase,
  Typography,
  FormControl,
  Select,
  Badge,
  Grid,
  styled,
  ClickAwayListener,
  Menu,
  MenuItem,
  Popover,
  ListItemButton,
} from "@mui/material";
import { StyledMenu, StyledMenuItem } from "../../components/StyledComponents/Menu/Menu.tsx";
import CloseIcon from "@mui/icons-material/Close";
import "reactjs-popup/dist/index.css";
import SearchIcon from "@mui/icons-material/Search";
import "../../App.css";
import {
  H6HeadlineSmall,
  BodyLarge,
  BodySmall,
  BodyMedium,
  H8TitleMedium,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import Styled from "@emotion/styled";
import {
  SecondaryButton,
  StyledIconButton,
} from "../../components/StyledComponents/Buttons/AuradineButtons.js";
import { OutlinedButton } from "../../components/StyledComponents/Buttons/AuradineButtons.js";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useTheme } from "../../ThemeContext/ThemeContext.js";

const CustomBadge = styled(Badge)(({ colors }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: colors?.primary?.main,
    color: colors?.primary?.contrastText,
    fontSize: "0.75rem", // Optional: Adjust font size
    padding: "0.25rem", // Optional: Adjust padding
  },
}));

const StyledPopoverContent = styled(Box)(({ colors }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "0.5rem",
  borderRadius: "1rem",
  boxShadow:
    "0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
}));

const StyledPopover = styled(Popover,)(({ colors }) => ({
  "& .MuiPaper-root": {
    borderRadius: "1rem", 
    backgroundColor: colors?.background?.popover,
  },
}));

const CommandCard = (props) => {
  const { colors } = useTheme();
  const {
    open,
    handleClose,
    anchorRef,
    setSelectedCommand,
    selectedCommand,
    setSelectedTune,
    setSelectedMode,
    setSelectedEventType,
    setSelectedCommands,
    selectedCommands,
    selectedTune,
    selectedMode,
    selectedEventType,
  } = props;

  const commandMapParents = {
    Mode: "fvmode",
    "Power Curtail": "powercurtail",
  };

  const commandMapMode = {
    "Mode,Eco": "eco",
    "Mode,Turbo": "turbo",
    "Mode,Custom": "custom",
  };

  const commandMapTune = {
    "Mode,Custom,Power": "power",
    "Mode,Custom,THs": "ths",
    "Mode,Custom,AutoTune": "autotune_v2",
  };

  const commandMapEventType = {
    "Power Curtail,Curtailment": "Curtailment",
    "Power Curtail,Scheduled Standby": "Scheduled Standby",
    "Power Curtail,Energy Optimizer": "Energy Optimizer",
  };

  const commandMap = {
    "QSE Query": "demandResponseQuery",
    "FluxVision Tuning": "accept-remote-tuning",
    "Firmware Upgrade": "firmware-upgrade",
    "Rolling Upgrade": "rolling-firmware-upgrade",
    "Flash LED": "led",
    "Pool Config": "fluxvisionconfig",
    "Restart": "restart",
    "Reset Curtail Schedules": "exitpcschedule",
    "Recover Boot Partition": "reimage",
    "Factory Reset": "factory-reset",
  };

  const commandMapAllParents = {
    Mode: "fvmode",
    "Power Curtail": "powercurtail",
    "QSE Query": "demandResponseQuery",
    "FluxVision Tuning": "accept-remote-tuning",
    "Firmware Upgrade": "firmware-upgrade",
    "Rolling Upgrade": "rolling-firmware-upgrade",
    "Flash LED": "led",
    "Pool Config": "fluxvisionconfig",
    "Restart": "restart",
    "Reset Curtail Schedules": "exitpcschedule",
    "Recover Boot Partition": "reimage",
    "Factory Reset": "factory-reset",
  };

  const updateUrlAndSessionStorage = (commands) => {
    const url = new URL(window.location.href);
    ["command", "mode", "tune", "eventType"].forEach((param) =>
      url.searchParams.delete(param)
    );

    const sessionParams = {
      command: [],
      mode: [],
      tune: [],
      eventType: [],
    };

    commands.forEach((command) => {
      if (command in commandMapParents) {
        const value = commandMapParents[command];
        url.searchParams.append("command", value);
        sessionParams.command.push(value);
      } else if (command in commandMap) {
        const value = commandMap[command];
        url.searchParams.append("command", value);
        sessionParams.command.push(value);
      } else if (command in commandMapMode) {
        const value = commandMapMode[command];
        url.searchParams.append("mode", value);
        sessionParams.mode.push(value);
      } else if (command in commandMapTune) {
        const value = commandMapTune[command];
        url.searchParams.append("tune", value);
        sessionParams.tune.push(value);
      } else if (command in commandMapEventType) {
        const value = commandMapEventType[command];
        url.searchParams.append("eventType", value);
        sessionParams.eventType.push(value);
      }
    });

    Object.entries(sessionParams).forEach(([key, values]) => {
      if (values.length > 0) {
        sessionStorage.setItem(key, JSON.stringify(values));
      } else {
        sessionStorage.removeItem(key);
      }
    });

    window.history.replaceState({}, "", url.toString());
  };

  const handleSelectCommand = (commandPath) => {
    if (selectedCommands.includes(commandPath)) return;

    const [firstLevelCommand, secondLevelCommand = "", thirdLevelCommand = ""] =
      commandPath.split(",");
    let filtered = [...selectedCommands];

    const removeItems = (items) => {
      filtered = filtered.filter((item) => !items.includes(item));
    };

    if (commandPath === "Mode") {
      setSelectedCommand((prev) =>
        prev.includes("fvmode") ? prev : [...prev, "fvmode"]
      );
      setSelectedMode([]);
      setSelectedTune([]);
      removeItems([
        "Mode,Custom",
        "Mode,Eco",
        "Mode,Turbo",
        "Mode,Custom,Power",
        "Mode,Custom,THs",
        "Mode,Custom,AutoTune",
      ]);
    }

    if (commandPath === "Power Curtail") {
      setSelectedCommand((prev) =>
        prev.includes("powercurtail") ? prev : [...prev, "powercurtail"]
      );
      setSelectedEventType([]);
      removeItems([
        "Power Curtail,Curtailment",
        "Power Curtail,Scheduled Standby",
        "Power Curtail,Energy Optimizer",
      ]);
    }

    if (["Eco", "Turbo", "Custom"].includes(secondLevelCommand)) {
      setSelectedCommand((prev) => prev.filter((mode) => mode !== "fvmode"));
      removeItems(["Mode"]);
      if (secondLevelCommand === "Eco") {
        setSelectedMode((prev) =>
          prev.includes("eco") ? prev : [...prev, "eco"]
        );
      } else if (secondLevelCommand === "Turbo") {
        setSelectedMode((prev) =>
          prev.includes("turbo") ? prev : [...prev, "turbo"]
        );
      } else if (secondLevelCommand === "Custom" && !thirdLevelCommand) {
        setSelectedMode((prev) =>
          prev.includes("custom") ? prev : [...prev, "custom"]
        );
        setSelectedTune([]);
        removeItems([
          "Mode,Custom,Power",
          "Mode,Custom,THs",
          "Mode,Custom,AutoTune",
        ]);
      }
    }

    if (
      ["Curtailment", "Scheduled Standby", "Energy Optimizer"].includes(
        secondLevelCommand
      )
    ) {
      setSelectedCommand((prev) =>
        prev.filter((mode) => mode !== "powercurtail")
      );
      removeItems(["Power Curtail"]);
      if (secondLevelCommand === "Curtailment") {
        setSelectedEventType((prev) =>
          prev.includes("Curtailment") ? prev : [...prev, "Curtailment"]
        );
      } else if (secondLevelCommand === "Scheduled Standby") {
        setSelectedEventType((prev) =>
          prev.includes("Scheduled Standby")
            ? prev
            : [...prev, "Scheduled Standby"]
        );
      } else if (secondLevelCommand === "Energy Optimizer") {
        setSelectedEventType((prev) =>
          prev.includes("Energy Optimizer")
            ? prev
            : [...prev, "Energy Optimizer"]
        );
      }
    }

    if (["Power", "THs", "AutoTune"].includes(thirdLevelCommand)) {
      setSelectedCommand((prev) => prev.filter((mode) => mode !== "fvmode"));
      setSelectedMode((prev) => prev.filter((mode) => mode !== "custom"));
      removeItems(["Mode,Custom", "Mode"]);
      if (thirdLevelCommand === "Power") {
        setSelectedTune((prev) =>
          prev.includes("power") ? prev : [...prev, "power"]
        );
      } else if (thirdLevelCommand === "THs") {
        setSelectedTune((prev) =>
          prev.includes("ths") ? prev : [...prev, "ths"]
        );
      } else if (thirdLevelCommand === "AutoTune") {
        setSelectedTune((prev) =>
          prev.includes("autotune_v2") ? prev : [...prev, "autotune_v2"]
        );
      }
    }

    if (commandPath in commandMap) {
      const command = commandMap[commandPath];
      setSelectedCommand((prev) =>
        prev.includes(command) ? prev : [...prev, command]
      );
    }
    const newCommands = [...filtered, commandPath];
    setSelectedCommands(newCommands);
    updateUrlAndSessionStorage(newCommands);
  };

  const handleDeselectCommand = (commandPath) => {
    if (!selectedCommands.includes(commandPath)) return;

    const [firstLevelCommand, secondLevelCommand = "", thirdLevelCommand = ""] =
      commandPath.split(",");
    let filtered = [...selectedCommands];

    const removeItems = (items) => {
      filtered = filtered.filter((item) => !items.includes(item));
    };

    if (commandPath === "Mode") {
      setSelectedCommand((prev) => prev.filter((item) => item !== "fvmode"));
      setSelectedMode([]);
      setSelectedTune([]);
      removeItems([
        "Mode",
        "Mode,Custom",
        "Mode,Eco",
        "Mode,Turbo",
        "Mode,Custom,Power",
        "Mode,Custom,THs",
        "Mode,Custom,AutoTune",
      ]);
    }

    if (commandPath === "Power Curtail") {
      setSelectedCommand((prev) =>
        prev.filter((item) => item !== "powercurtail")
      );
      setSelectedEventType([]);
      removeItems([
        "Power Curtail",
        "Power Curtail,Curtailment",
        "Power Curtail,Scheduled Standby",
        "Power Curtail,Energy Optimizer",
      ]);
    }

    if (["Eco", "Turbo", "Custom"].includes(secondLevelCommand)) {
      removeItems([commandPath]);
      if (secondLevelCommand === "Eco") {
        setSelectedMode((prev) => prev.filter((item) => item !== "eco"));
      } else if (secondLevelCommand === "Turbo") {
        setSelectedMode((prev) => prev.filter((item) => item !== "turbo"));
      } else if (secondLevelCommand === "Custom" && !thirdLevelCommand) {
        setSelectedMode((prev) => prev.filter((item) => item !== "custom"));
        setSelectedTune([]);
        removeItems([
          "Mode,Custom,Power",
          "Mode,Custom,THs",
          "Mode,Custom,AutoTune",
        ]);
      }
    }

    if (
      ["Curtailment", "Scheduled Standby", "Energy Optimizer"].includes(
        secondLevelCommand
      )
    ) {
      removeItems([commandPath]);
      if (secondLevelCommand === "Curtailment") {
        setSelectedEventType((prev) =>
          prev.filter((item) => item !== "Curtailment")
        );
      } else if (secondLevelCommand === "Scheduled Standby") {
        setSelectedEventType((prev) =>
          prev.filter((item) => item !== "Scheduled Standby")
        );
      } else if (secondLevelCommand === "Energy Optimizer") {
        setSelectedEventType((prev) =>
          prev.filter((item) => item !== "Energy Optimizer")
        );
      }
    }

    if (["Power", "THs", "AutoTune"].includes(thirdLevelCommand)) {
      removeItems([commandPath]);
      if (thirdLevelCommand === "Power") {
        setSelectedTune((prev) => prev.filter((item) => item !== "power"));
      } else if (thirdLevelCommand === "THs") {
        setSelectedTune((prev) => prev.filter((item) => item !== "ths"));
      } else if (thirdLevelCommand === "AutoTune") {
        setSelectedTune((prev) =>
          prev.filter((item) => item !== "autotune_v2")
        );
      }
    }

    if (commandPath in commandMap) {
      setSelectedCommand((prev) =>
        prev.filter((item) => item !== commandMap[commandPath])
      );
      removeItems([commandPath]);
    }
    setSelectedCommands([...filtered]);
    updateUrlAndSessionStorage(filtered);
  };

  React.useEffect(() => {
    if (!selectedCommand) return;

    let commandsToAdd = [];
    Object.entries(commandMapAllParents).forEach(([parent, value]) => {
      if (selectedCommand.includes(value)) {
        commandsToAdd.push(parent);
      }
    });

    Object.entries(commandMapMode).forEach(([key, value]) => {
      if (selectedMode.includes(value)) {
        commandsToAdd.push(key);
      }
    });
    Object.entries(commandMapTune).forEach(([key, value]) => {
      if (selectedTune.includes(value)) {
        commandsToAdd.push(key);
      }
    });

    Object.entries(commandMapEventType).forEach(([key, value]) => {
      if (selectedEventType.includes(value)) {
        commandsToAdd.push(key);
      }
    });

    if (commandsToAdd.length > 0) {
      setSelectedCommands((prev) => {
        const updated = [...new Set([...prev, ...commandsToAdd])];
        return updated;
      });
    }
  }, [selectedCommand, selectedEventType, selectedMode, selectedTune]);

  const commandsList = [
    {
      commandName: "Mode",
      secondLevelCommand: [
        { secondLevelCommandName: "Eco" },
        { secondLevelCommandName: "Turbo" },
        {
          secondLevelCommandName: "Custom",
          thirdLevelCommand: [
            { thirdLevelCommandName: "Power" },
            { thirdLevelCommandName: "THs" },
            { thirdLevelCommandName: "AutoTune" },
          ],
        },
      ],
    },
    {
      commandName: "Power Curtail",
      secondLevelCommand: [
        { secondLevelCommandName: "Curtailment" },
        { secondLevelCommandName: "Scheduled Standby" },
        {
          secondLevelCommandName: "Energy Optimizer",
        },
      ],
    },
    { commandName: "QSE Query" },
    { commandName: "FluxVision Tuning" },
    { commandName: "Firmware Upgrade" },
    { commandName: "Rolling Upgrade" },
    { commandName: "Flash LED" },
    { commandName: "Pool Config" },
    { commandName: "Restart" },
    { commandName: "Reset Curtail Schedules" },
    { commandName: "Recover Boot Partition" },
    { commandName: "Factory Reset" },
  ];

  return (
    <StyledPopover
      colors={colors}
      sx={{
        zIndex: 9999,
      }}
      open={open}
      anchorEl={anchorRef.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <StyledPopoverContent colors={colors}>
          <Box
            display="flex"
            flexDirection={"column"}
            padding="0.5rem"
            className={"search-box"}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              width={"18rem"}
              minWidth={"18rem"}
              maxHeight={"18rem"}
              minHeight={"18rem"}
              maxWidth={"45rem"}
              overflow={"auto"}
            >
              {commandsList.map((command, commandIndex) => {
                const firstPath = command.commandName;

                return (
                  <React.Fragment key={commandIndex}>
                    <ListItemButton
                      selected={selectedCommands.includes(firstPath)}
                      onClick={() => handleSelectCommand(firstPath)}
                      sx={{
                        minHeight: "3rem",
                        maxHeight: "3rem",
                        "&:hover": {
                          backgroundColor: colors?.hover?.muted,  
                          borderRadius: "6.5rem",
                          cursor: "pointer",
                        },
                        "&.Mui-selected": {
                          borderRadius: "6.5rem",
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <H8TitleMedium>{command.commandName}</H8TitleMedium>
                        {selectedCommands.includes(firstPath) && (
                          <CloseIcon
                            fontSize="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeselectCommand(firstPath);
                            }}
                            style={{ cursor: "pointer" }}
                            sx={{ color: colors?.text?.muted }}  
                          />
                        )}
                      </Box>
                    </ListItemButton>

                    {command.secondLevelCommand?.map(
                      (secondCommand, secondIndex) => {
                        const secondPath = `${command.commandName},${secondCommand.secondLevelCommandName}`;

                        return (
                          <React.Fragment key={secondIndex}>
                            <ListItemButton
                              selected={selectedCommands.includes(secondPath)}
                              onClick={() =>
                                handleSelectCommand(secondPath, [firstPath])
                              }
                              sx={{
                                paddingLeft: "2.5rem",
                                minHeight: "3rem",
                                maxHeight: "3rem",
                                "&:hover": {
                                  backgroundColor: colors?.hover?.muted,  
                                  borderRadius: "6.5rem",
                                  cursor: "pointer",
                                },
                                "&.Mui-selected": {
                                  borderRadius: "6.5rem",
                                },
                              }}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                width="100%"
                              >
                                <H8TitleMedium sx={{fontWeight: 400}}>{secondCommand.secondLevelCommandName}</H8TitleMedium>
                                {selectedCommands.includes(secondPath) && (
                                  <CloseIcon
                                    fontSize="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeselectCommand(secondPath);
                                    }}
                                    style={{ cursor: "pointer" }}
                                    sx={{ color: colors?.text?.muted }}
                                  />
                                )}
                              </Box>
                            </ListItemButton>

                            {secondCommand.thirdLevelCommand?.map(
                              (thirdCommand, thirdIndex) => {
                                const thirdPath = `${command.commandName},${secondCommand.secondLevelCommandName},${thirdCommand.thirdLevelCommandName}`;

                                return (
                                  <ListItemButton
                                    key={thirdIndex}
                                    selected={selectedCommands.includes(
                                      thirdPath
                                    )}
                                    onClick={() =>
                                      handleSelectCommand(thirdPath, [
                                        firstPath,
                                        secondPath,
                                      ])
                                    }
                                    sx={{
                                      paddingLeft: "5rem",
                                      minHeight: "3rem",
                                      maxHeight: "3rem",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.muted,  
                                        borderRadius: "6.5rem",
                                        cursor: "pointer",
                                      },
                                      "&.Mui-selected": {
                                        borderRadius: "6.5rem",
                                      },
                                    }}
                                  >
                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                      width="100%"
                                    >
                                      <H8TitleMedium sx={{fontWeight: 400}}>{thirdCommand.thirdLevelCommandName}</H8TitleMedium>
                                      {selectedCommands.includes(thirdPath) && (
                                        <CloseIcon
                                          fontSize="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeselectCommand(thirdPath);
                                          }}
                                          style={{
                                            cursor: "pointer",
                                            marginLeft: "0.5rem",
                                          }}
                                          sx={{ color: colors?.text?.muted }}
                                        />
                                      )}
                                    </Box>
                                  </ListItemButton>
                                );
                              }
                            )}
                          </React.Fragment>
                        );
                      }
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        </StyledPopoverContent>
      </ClickAwayListener>
    </StyledPopover>
  );
};

export const JobsPageHeader = (props) => {
  const {
    selectedStatus,
    setSelectedStatus,
    selectedCommand,
    setSelectedCommand,
    selectedTune,
    setSelectedTune,
    selectedMode,
    setSelectedMode,
    selectedEventType,
    setSelectedEventType,
    selectedTimeRange,
    setSelectedTimeRange,
    onSearchChange,
    searchValue,
    setSearchValue,
    setDsearchValue,
  } = props;
  const { colors } = useTheme();
  const [selectedCommands, setSelectedCommands] = React.useState([]);
  const [anchorElStatus, setAnchorElStatus] = React.useState(null);
  const buttonRefStatus = React.useRef(null);
  const badgeContentStatus = selectedStatus ? selectedStatus.length : null;
  const statusList = [
    { label: "Completed", value: "completed" },
    { label: "Failed", value: "failed" },
    { label: "Pending", value: "pending" },
  ];

  const [anchorElCommands, setAnchorElCommands] = React.useState(null);
  const buttonRefCommands = React.useRef(null);
  const badgeContentCommands = selectedCommands
    ? selectedCommands.length
    : null;

  const [anchorElTimeRange, setAnchorElTimeRange] = React.useState(null);
  const buttonRefTimeRange = React.useRef(null);
  const timeRangeList = [
    { label: "Last 2 minutes", value: "last_2_min" },
    { label: "Last 5 minutes", value: "last_5_min" },
    { label: "Last 15 minutes", value: "last_15_min" },
    { label: "Last 30 minutes", value: "last_30_min" },
    { label: "Last 1 hour", value: "last_1_hr" },
    { label: "Last 1 day", value: "last_1_day" },
    { label: "Last 2 days", value: "last_2_days" },
  ];
  function getLabelByValue(value) {
    const found = timeRangeList.find((item) => item.value === value);
    return found ? found.label : null;
  }

  const handleClickAwayStatus = () => {
    setAnchorElStatus(null);
  };

  const setMenuOpenStatus = () => {
    setAnchorElStatus(buttonRefStatus.current);
  };
  const handleClickAwayCommands = () => {
    setAnchorElCommands(null);
  };

  const setMenuOpenCommands = () => {
    setAnchorElCommands(buttonRefCommands.current);
  };

  const handleClickAwayTimeRange = () => {
    setAnchorElTimeRange(null);
  };

  const setMenuOpenTimeRange = () => {
    setAnchorElTimeRange(buttonRefTimeRange.current);
  };

  const toggleSelectionStatus = (value) => {
    setSelectedStatus((currentSelectedStatus) => {
      const updatedStatus = currentSelectedStatus.includes(value)
        ? currentSelectedStatus.filter((status) => status !== value)
        : [...currentSelectedStatus, value];

      const url = new URL(window.location.href);
      url.searchParams.delete("status");
      updatedStatus.forEach((status) => {
        url.searchParams.append("status", status);
      });
      window.history.replaceState({}, "", url.toString());
      updatedStatus.length !== 0
        ? sessionStorage.setItem("status", JSON.stringify(updatedStatus))
        : sessionStorage.removeItem("status");

      return updatedStatus;
    });
  };

  const handleSelectTimeRange = (value) => {
    const url = new URL(window.location.href);
    url.searchParams.delete("timeRange");
    if (selectedTimeRange === value) {
      setSelectedTimeRange(null);
      url.searchParams.delete("timeRange");
      sessionStorage.removeItem("timeRange");
    } else {
      setSelectedTimeRange(value);
      url.searchParams.set("timeRange", value);
      sessionStorage.setItem("timeRange", JSON.stringify(value));
    }
    window.history.replaceState({}, "", url.toString());
  };

  const handleClearFilters = () => {
    setSelectedCommands([]);
    setSelectedCommand([]);
    setSelectedMode([]);
    setSelectedEventType([]);
    setSelectedTune([]);
    setSelectedTimeRange("");
    setSelectedStatus([]);
    setSearchValue("");
    setDsearchValue("");
    const url = new URL(window.location.href);
    url.search = "";
    window.history.replaceState({}, "", url);
    const storageToClear = [
      "command",
      "mode",
      "tune",
      "powercurtail",
      "timeRange",
      "status",
      "eventType",
      "search",
    ];
    storageToClear.forEach((key) => {
      sessionStorage.removeItem(key);
    });
  };

  return (
    <Box display="flex" justifyContent={"space-between"} alignItems="center">
      <Box
        display="flex"
        justifyContent={"flex-start"}
        gap={6}
        sx={{ height: "100%" }}
      >
        <Paper
          component="form"
          elevation={0}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            backgroundColor: colors?.background?.secondary,
            borderRadius: "1.75rem",
            paddingLeft: "1rem",
            width: "100%",
          }}
        >
          <Tooltip disableFocusListener disableTouchListener>
            <InputBase
              sx={{ flex: 1, width: "22.5rem", color: colors?.text?.primary }}
              value={searchValue}
              placeholder="Search for email or job ID"
              onChange={(event) => {
                const userInput = event.target.value;
                onSearchChange(userInput);
                const url = new URL(window.location.href);
                if (userInput === "") {
                    url.searchParams.delete("search");
                } else {
                    url.searchParams.set("search", userInput);
                }
                window.history.replaceState({}, "", url.toString());
                sessionStorage.setItem("search", JSON.stringify(userInput));
              }}
              inputProps={{
                "aria-label": "search",
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                }
              }}
            />
          </Tooltip>
          <StyledIconButton colors={colors} disabled aria-label="search">
            <SearchIcon />
          </StyledIconButton>
        </Paper>
        <Box display="flex" flexDirection={"row"} gap={6} alignItems={"center"}>
          <ClickAwayListener onClickAway={handleClickAwayStatus}>
            <>
              <CustomBadge badgeContent={badgeContentStatus} colors={colors}>
                <OutlinedButton
                  colors={colors}
                  onClick={setMenuOpenStatus}
                  endIcon={
                    Boolean(anchorElStatus) ? (
                      <KeyboardArrowUpRoundedIcon />
                    ) : (
                      <KeyboardArrowDownRoundedIcon />
                    )
                  }
                  ref={buttonRefStatus}
                >
                  Status
                </OutlinedButton>
              </CustomBadge>

              <StyledMenu
                colors={colors}
                anchorEl={anchorElStatus}
                open={Boolean(anchorElStatus)}
                onClose={handleClickAwayStatus}
              >
                {statusList &&
                  statusList.map((status, index) => (
                    <StyledMenuItem
                      colors={colors}
                      key={`menuItem${index}`}
                      selected={selectedStatus.includes(status.value)}
                      onClick={() => {
                        if (!selectedStatus.includes(status.value)) {
                          toggleSelectionStatus(status.value);
                        }
                      }}
                      sx={{ minWidth: "12rem", display: "flex",
                        justifyContent: "space-between"}}
                    >
                      {status.label}
                      {selectedStatus.includes(status.value) && (
                        <CloseIcon
                          fontSize="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelectionStatus(status.value);
                          }}
                          style={{ cursor: "pointer" }}
                          sx={{ color: colors?.text?.muted }}
                        />
                      )}
                    </StyledMenuItem>
                  ))}
              </StyledMenu>
            </>
          </ClickAwayListener>
          <ClickAwayListener onClickAway={handleClickAwayCommands}>
            <>
              <CustomBadge badgeContent={badgeContentCommands} colors={colors}>
                <OutlinedButton
                  colors={colors}
                  onClick={setMenuOpenCommands}
                  endIcon={
                    Boolean(anchorElCommands) ? (
                      <KeyboardArrowUpRoundedIcon />
                    ) : (
                      <KeyboardArrowDownRoundedIcon />
                    )
                  }
                  ref={buttonRefCommands}
                >
                  Command
                </OutlinedButton>
              </CustomBadge>

              <CommandCard
                open={Boolean(anchorElCommands)}
                handleClose={handleClickAwayCommands}
                anchorRef={buttonRefCommands}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                selectedCommand={selectedCommand}
                setSelectedCommand={setSelectedCommand}
                selectedTune={selectedTune}
                setSelectedTune={setSelectedTune}
                selectedMode={selectedMode}
                setSelectedMode={setSelectedMode}
                selectedEventType={selectedEventType}
                setSelectedEventType={setSelectedEventType}
                selectedTimeRange={selectedTimeRange}
                setSelectedTimeRange={setSelectedTimeRange}
                onSearchChange={onSearchChange}
                selectedCommands={selectedCommands}
                setSelectedCommands={setSelectedCommands}
              />
            </>
          </ClickAwayListener>

          <ClickAwayListener onClickAway={handleClickAwayTimeRange}>
            <>
              <Box
                sx={{
                  minWidth: "fit-content",
                }}
              >
                <OutlinedButton
                  colors={colors}
                  onClick={setMenuOpenTimeRange}
                  endIcon={
                    Boolean(anchorElTimeRange) ? (
                      <KeyboardArrowUpRoundedIcon />
                    ) : (
                      <KeyboardArrowDownRoundedIcon />
                    )
                  }
                  ref={buttonRefTimeRange}
                >
                  {selectedTimeRange ? (
                    <BodyMedium>
                      {getLabelByValue(selectedTimeRange)}
                    </BodyMedium>
                  ) : (
                    "Time Range"
                  )}
                </OutlinedButton>
              </Box>

              <StyledMenu
                colors={colors}
                anchorEl={anchorElTimeRange}
                open={Boolean(anchorElTimeRange)}
                onClose={handleClickAwayTimeRange}
              >
                {timeRangeList &&
                  timeRangeList.map((timeRange, index) => (
                    <StyledMenuItem
                    colors={colors}
                      key={`menuItem${index}`}
                      selected={selectedTimeRange === timeRange.value}
                      onClick={() => {
                        if (selectedTimeRange !== timeRange.value) {
                          handleSelectTimeRange(timeRange.value);
                        }
                      }}
                      sx={{ minWidth: "12rem", display: "flex",
                        justifyContent: "space-between", }}
                    >
                      {timeRange.label}
                      {selectedTimeRange === timeRange.value && (
                        <CloseIcon
                          fontSize="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectTimeRange(timeRange.value);
                          }}
                          style={{ cursor: "pointer" }}
                          sx={{ color: colors?.text?.muted }}
                        />
                      )}
                    </StyledMenuItem>
                  ))}
              </StyledMenu>
            </>
          </ClickAwayListener>
        </Box>
      </Box>
      <Box display="flex" paddingLeft={"2rem"}>
        <SecondaryButton colors={colors} onClick={handleClearFilters}>
          Clear Filters
        </SecondaryButton>
      </Box>
    </Box>
  );
};
